import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, Download, AlertTriangle, CheckCircle } from 'lucide-react';
import { PageHeader, SectionCard } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { API_BASE } from '../api';

interface PreviewRow {
  row: number;
  data: Record<string, string>;
  errors: string[];
}

const CSV_TEMPLATE = `title,title_zh,description,category,tags,price_min,price_max,moq,lead_time_days,is_test
"Sample T-Shirt","示例T恤","High quality cotton t-shirt","apparel","cotton,casual",12.50,29.99,10,7,false
"Phone Case","手机壳","Premium silicone phone case","accessories","phone,tech",3.00,15.99,50,5,false`;

export default function VendorCsvImport() {
  const { t } = useTranslation(['products', 'common']);
  const { toasts, addToast, removeToast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parsing, setParsing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState<{ total: number; valid: number; errors: number; rows: PreviewRow[] } | null>(null);

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    try {
      const text = await file.text();
      const res = await fetch(`${API_BASE}/vendor/products/import-csv`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'text/csv' },
        body: text,
      });
      const json = await res.json();
      if (res.ok) {
        setPreview(json.data);
      } else {
        addToast(json.error || t('common:error'), 'error');
      }
    } catch {
      addToast(t('common:error'), 'error');
    } finally {
      setParsing(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    const validRows = preview.rows.filter((r) => r.errors.length === 0);
    if (validRows.length === 0) { addToast('No valid rows to import', 'error'); return; }

    setConfirming(true);
    try {
      const res = await fetch(`${API_BASE}/vendor/products/import-csv/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: validRows }),
      });
      const json = await res.json();
      if (res.ok) {
        addToast(t('products:csv.success', { count: json.data.created }), 'success');
        setTimeout(() => navigate('/vendor/products'), 1500);
      } else {
        addToast(json.error || t('common:error'), 'error');
      }
    } catch {
      addToast(t('common:error'), 'error');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        title={t('products:csv.title')}
        back={{ label: t('products:backToProducts'), onClick: () => navigate('/vendor/products') }}
      />

      <SectionCard>
        <div className="p-5 space-y-4">
          <p className="text-sm text-white/60">{t('products:csv.instructions')}</p>
          <button onClick={downloadTemplate} className="btn-ghost flex items-center gap-2 text-sm">
            <Download className="w-4 h-4" />
            {t('products:csv.downloadTemplate')}
          </button>
        </div>
      </SectionCard>

      {!preview && (
        <SectionCard>
          <div
            className="p-8 flex flex-col items-center justify-center gap-3 text-white/30 cursor-pointer"
            style={{ border: '2px dashed rgba(255,255,255,0.08)', borderRadius: '4px', margin: '20px' }}
            onClick={() => fileInputRef.current?.click()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
            onDragOver={(e) => e.preventDefault()}
          >
            {parsing ? (
              <div className="w-6 h-6 border-2 border-[#FFD400] border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Upload className="w-8 h-8" />
                <p className="text-sm">{t('products:csv.dragDrop')}</p>
              </>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
        </SectionCard>
      )}

      {preview && (
        <>
          <SectionCard title={t('products:csv.preview')}>
            <div className="p-5">
              <div className="flex items-center gap-4 mb-4 text-sm">
                <span className="text-white/70">{t('products:csv.rowsFound', { count: preview.total })}</span>
                <span className="flex items-center gap-1 text-[#4ade80]">
                  <CheckCircle className="w-3.5 h-3.5" /> {preview.valid} valid
                </span>
                {preview.errors > 0 && (
                  <span className="flex items-center gap-1 text-[#f87171]">
                    <AlertTriangle className="w-3.5 h-3.5" /> {t('products:csv.errors', { count: preview.errors })}
                  </span>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left px-3 py-2 text-xs text-white/40">Row</th>
                      <th className="text-left px-3 py-2 text-xs text-white/40">Title</th>
                      <th className="text-left px-3 py-2 text-xs text-white/40">Category</th>
                      <th className="text-left px-3 py-2 text-xs text-white/40">Price</th>
                      <th className="text-left px-3 py-2 text-xs text-white/40">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 50).map((row) => (
                      <tr key={row.row} className="border-b border-white/[0.03]">
                        <td className="px-3 py-2 text-white/30">{row.row}</td>
                        <td className="px-3 py-2 text-white/80">{row.data.title || '—'}</td>
                        <td className="px-3 py-2 text-white/50">{row.data.category || 'general'}</td>
                        <td className="px-3 py-2 text-white/50">
                          {row.data.price_min ? `$${row.data.price_min}` : '—'}
                          {row.data.price_max ? ` – $${row.data.price_max}` : ''}
                        </td>
                        <td className="px-3 py-2">
                          {row.errors.length > 0 ? (
                            <span className="text-xs text-[#f87171]">{row.errors.join('; ')}</span>
                          ) : (
                            <CheckCircle className="w-3.5 h-3.5 text-[#4ade80]" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </SectionCard>

          <div className="flex items-center gap-3">
            <button onClick={handleConfirm} disabled={confirming || preview.valid === 0} className="btn-primary flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {confirming ? '...' : t('products:csv.confirm')}
            </button>
            <button onClick={() => setPreview(null)} className="btn-ghost">{t('products:csv.cancel')}</button>
          </div>
        </>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
